import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { getSafeClient } from "@/integrations/supabase/safeClient";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Loader2, Search } from "lucide-react";
import RatingComponent from "@/components/RatingComponent";
import EmptyState from "@/components/EmptyState";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

interface ReviewData {
  id: string;
  rating: number;
  text: string | null;
  created_at: string;
  offer_id: string;
  reviewer_id: string;
  profiles: {
    name: string | null;
    avatar_url: string | null;
  } | null;
  offers: {
    title: string;
  } | null;
  review_type: 'offer' | 'profile';
}

const Reviews = () => {
  const fetchReviews = async (): Promise<ReviewData[]> => {
    // Mock implementation until auth is set up
    return [];
  };

  const { data: reviews, isLoading, error } = useQuery({
    queryKey: ["reviews"],
    queryFn: fetchReviews,
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <>
      <Helmet>
        <title>Customer Reviews | SEO Link Market</title>
      </Helmet>
      <div className="bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl font-bold mb-2">Customer Reviews</h1>
          <p className="text-gray-600 mb-8">
            See what our customers say about their experience with SEO Link Market sellers
          </p>

          {isLoading ? (
            <div className="flex justify-center items-center p-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2">Loading reviews...</span>
            </div>
          ) : error ? (
            <EmptyState 
              title="Error loading reviews" 
              description="Please try again later" 
              icon="error" 
            />
          ) : reviews && reviews.length > 0 ? (
            <div className="space-y-6">
              {reviews.map((review) => (
                <Card key={review.id} className="overflow-hidden">
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Avatar>
                            {review.profiles?.avatar_url ? (
                              <AvatarImage
                                src={review.profiles.avatar_url}
                                alt={review.profiles.name || "User"}
                              />
                            ) : (
                              <AvatarFallback>
                                {review.profiles?.name ? review.profiles.name.charAt(0) : "U"}
                              </AvatarFallback>
                            )}
                          </Avatar>
                          <div>
                            <div className="font-medium">{review.profiles?.name || "Anonymous User"}</div>
                            <div className="text-sm text-gray-500">
                              For offer: <span className="text-brand-blue">{review.offers?.title}</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatDate(review.created_at)}
                        </div>
                      </div>

                      <div>
                        <RatingComponent value={review.rating} readOnly size="md" />
                        <p className="mt-2 text-gray-700">
                          {review.text || "No comment provided."}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <EmptyState 
              title="No reviews yet" 
              description="Be the first to leave a review for a seller" 
              icon="inbox" 
            />
          )}
        </div>
      </div>
    </>
  );
};

export default Reviews;
